import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    applications,
    useCurrentApplication,
    useCurrentClient,
    useCurrentUser,
} from '@alli/frontend-common';
import { OpenFeatureProvider } from '@openfeature/react-sdk';
import Header from './components/Header';

function shouldUpdateLocalClient(localCurrentClient, currentClient) {
    if (!localCurrentClient && currentClient) {
        return true;
    }

    return currentClient && currentClient.id !== localCurrentClient.id;
}

export default function Root({ location, singleSpa }) {
    const currentUser = useCurrentUser();
    const currentApp = useCurrentApplication();

    const currentClient = useCurrentClient();
    // copy the current client locally, so we can immediately change it
    // for ui updates and such, but we also have the effect here to trigger
    // the change if coming from the outside world.
    const [localCurrentClient, setLocalCurrentClient] = useState(currentClient);
    useEffect(() => {
        if (shouldUpdateLocalClient(localCurrentClient, currentClient)) {
            setLocalCurrentClient(currentClient);
        }
    }, [currentClient, setLocalCurrentClient, localCurrentClient]);

    // track clients in state based on the user, theoretically
    // this user is only changed if we get new data back from the API
    // so fairly "safe"
    const [clients, setClients] = useState(currentUser?.clients || []);
    const clientsMap = useRef(
        currentUser?.clients.reduce(
            (result, client) => {
                result.bySlug[client.slug] = client;
                result.byId[client.id] = client;
                return result;
            },
            {
                bySlug: {},
                byId: {},
            },
        ),
    );

    useEffect(() => {
        if (!currentUser) {
            return;
        }
        const clientsMap = {
            bySlug: {},
            byId: {},
        };
        const clients = currentUser.clients.map(client => {
            clientsMap.bySlug[client.slug] = client;
            clientsMap.byId[client.id] = client;
            return client;
        });

        // admins can access all clients, so we may see a client in currentClient
        // that's not in the clients array above. Add it if so.
        if (currentClient && !clientsMap.bySlug[currentClient.slug]) {
            let cc = {
                ...currentClient,
            };
            clients.push(cc);
            clientsMap.bySlug[currentClient.slug] = cc;
            clientsMap.byId[currentClient.id] = cc;
        }

        setClients(clients);
        clientsMap.current = clientsMap;
    }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

    // ensure that changes to current client reflect in the clients array and
    // thus shown in the sidebar. Note that this _does not erase_ the client
    // if the current client is switched. This is somewhat by design and...
    // somewhat because of laziness.
    useEffect(() => {
        if (!currentClient) {
            return;
        }

        if (
            typeof clientsMap.current.bySlug[currentClient.slug] !== 'undefined'
        ) {
            return;
        }

        const cc = {
            ...currentClient,
        };

        clientsMap.current.bySlug[cc.slug] = cc;
        clientsMap.current.byId[cc.id] = cc;
        setClients([cc, ...clients]);
    }, [currentClient]); // eslint-disable-line react-hooks/exhaustive-deps

    const changeClient = useCallback(
        slug => {
            const newClient = clientsMap.current.bySlug[slug];
            const newUrl = applications.renderClientUrl(newClient, currentApp);

            setLocalCurrentClient(newClient);
            singleSpa.navigateToUrl(newUrl);
        },
        [singleSpa, currentApp, setLocalCurrentClient],
    );

    return (
        <OpenFeatureProvider>
            <Header
                clients={clients}
                currentClient={localCurrentClient || clients[0]}
                changeClient={changeClient}
                currentApp={currentApp.activeInSidebar || currentApp.id}
                activeSubnavItem={currentApp.activeSubnavItem}
                currentUser={currentUser}
                navigateToUrl={singleSpa.navigateToUrl}
            />
        </OpenFeatureProvider>
    );
}
